import React, { useState } from "react";
import {
    CloudUpload,
    FileText,
    ChevronLeft,
    ChevronRight,
    Undo2,
    Loader2,
    Square,
    Ruler,
    Dot,
    PenTool,
    Check,
    X as XIcon,
    Printer,
    Signature,
} from "lucide-react";
import PdfViewer from "../PdfViewer";
import ScaleIndicator from "../ScaleIndicator";
import AsBuiltCanvas from "./AsBuiltCanvas";
import SignaturePadDialog from "./SignaturePadDialog";

const isImage = (f) =>
    f &&
    ((f.content_type && f.content_type.startsWith("image/")) ||
        // iter-167 — include HEIC/HEIF; backend transcodes them to
        // JPEG on the fly when the URL is fetched with ?render=web.
        /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i.test(f.original_filename || ""));

const isPdf = (f) =>
    f &&
    ((f.content_type && f.content_type === "application/pdf") ||
        /\.pdf$/i.test(f.original_filename || ""));

const TRADES = [
    { id: "earthwork", name: "Earthwork", color: "#E67E22" },
    { id: "concrete", name: "Concrete", color: "#A3A3A3" },
    { id: "framing", name: "Framing", color: "#27AE60" },
    { id: "plumbing", name: "Plumbing", color: "#3498DB" },
    { id: "electrical", name: "Electrical", color: "#E74C3C" },
    { id: "roofing", name: "Roofing", color: "#8E44AD" },
    { id: "site_utilities", name: "Site Utilities", color: "#D35400" },
    { id: "general", name: "General", color: "#000000" },
];

/**
 * Main workspace canvas — PDF viewer / image preview / empty state + the
 * floating page-nav pill. Extracted from WorkspacePage.jsx in iter-20. All
 * props are existing WorkspacePage locals; no behaviour changes.
 */
export default function WorkspaceCanvas({
    loading,
    activeFile,
    fileUrl,
    backendUrl,
    token,
    onAsBuiltUpdated,
    activePage,
    project,
    navPages = [],
    navIndex,
    goToNav,
    onUploadClick,
    onScaleChange,
    takeoffItems = [],
    selectedTakeoffId = null,
    onSelectTakeoff,
    onCommitTakeoffPoints,
    canUndoTakeoff = false,
    onUndoTakeoff,
    undoingTakeoff = false,
    drawMode = null,
    drawTrade = "earthwork",
    drawPoints = [],
    drawSaving = false,
    onDrawModeChange,
    onDrawTradeChange,
    onDrawPoint,
    onDrawFinish,
    onDrawCancel,
    onPageSize,
    onScheduleFieldChange,
    onPrintSchedule,
    onSaveSignature,
}) {
    const [signatureOpen, setSignatureOpen] = useState(false);
    return (
        <main className="flex-1 min-w-0 flex flex-col bg-[#F2F4F8]">
            <div
                className="flex-1 min-h-0 overflow-hidden flex"
                data-testid="workspace-canvas"
            >
                {loading ? (
                    <div className="m-auto text-slate-500 text-sm">
                        Loading workspace…
                    </div>
                ) : !activeFile ? (
                    <div className="m-auto text-center text-slate-500 max-w-sm p-6">
                        <FileText className="w-10 h-10 mx-auto mb-3 text-slate-400" />
                        <div className="font-semibold text-slate-700 mb-1">
                            No plans attached yet
                        </div>
                        <div className="text-xs">
                            Upload PDF blueprints or drawings to start a takeoff.
                        </div>
                        <button
                            onClick={onUploadClick}
                            className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#007AFF] text-white text-sm font-semibold hover:bg-[#3395FF]"
                        >
                            <CloudUpload className="w-4 h-4" /> Upload plans
                        </button>
                    </div>
                ) : (
                    <div className="w-full h-full bg-white border border-slate-300 overflow-hidden flex flex-col relative">
                        {/* iter-169q — Hide the scale indicator on
                            As-Built canvases and Schedule 1/2/3 PDFs.
                            None of those have a drawing scale so the
                            "1:50" pill was just visual noise. */}
                        {!activeFile.is_asbuilt && !activeFile.schedule_no && (
                            <ScaleIndicator
                                file={activeFile}
                                unitSystem={project?.unit_system || "imperial"}
                                onChange={onScaleChange}
                            />
                        )}
                        {isImage(activeFile) && !activeFile.is_asbuilt && (
                            <img
                                src={fileUrl}
                                alt={activeFile.original_filename}
                                className="max-w-full max-h-full object-contain"
                            />
                        )}
                        {isPdf(activeFile) && !activeFile.is_asbuilt && (
                            <PdfViewer
                                url={fileUrl}
                                targetPage={activePage}
                                takeoffItems={takeoffItems}
                                selectedTakeoffId={selectedTakeoffId}
                                onSelectTakeoff={onSelectTakeoff}
                                onCommitTakeoffPoints={onCommitTakeoffPoints}
                                unitSystem={project?.unit_system || "imperial"}
                                drawMode={drawMode}
                                drawTrade={drawTrade}
                                drawPoints={drawPoints}
                                onDrawPoint={onDrawPoint}
                                onDrawFinish={onDrawFinish}
                                onPageSize={onPageSize}
                                interactiveForm={Boolean(activeFile.schedule_no)}
                                onFormFieldChange={onScheduleFieldChange}
                            />
                        )}
                        {/* iter-177 — top-right floating cluster for
                            Schedule PDFs. Print / Flatten was removed
                            per user request; use the "Print pack" button
                            in the AI toolbar to download all three
                            schedules flattened as a ZIP. The Sign
                            button stays for Schedule 2 signatures. */}
                        {isPdf(activeFile) && activeFile.schedule_no === 2 && onSaveSignature && (
                            <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
                                <button
                                    data-testid="btn-open-signature-pad"
                                    onClick={() => setSignatureOpen(true)}
                                    className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#007AFF] text-white text-sm font-semibold shadow-lg hover:bg-[#0066CC] transition"
                                    title="Draw applicant signature for Section E"
                                >
                                    <Signature className="w-4 h-4" />
                                    Sign
                                </button>
                            </div>
                        )}
                        <SignaturePadDialog
                            open={signatureOpen}
                            onOpenChange={setSignatureOpen}
                            onSave={(png, saveToProfile) => {
                                if (onSaveSignature && activeFile) {
                                    onSaveSignature(activeFile.id, png, saveToProfile);
                                }
                            }}
                        />
                        {activeFile.is_asbuilt && (
                            <AsBuiltCanvas
                                activeFile={activeFile}
                                backendUrl={backendUrl}
                                token={token}
                                project={project}
                                onUpdated={onAsBuiltUpdated}
                            />
                        )}
                        {!activeFile.is_asbuilt && !isImage(activeFile) && !isPdf(activeFile) && (
                            <div className="p-6 text-center text-slate-600">
                                <FileText className="w-10 h-10 mx-auto mb-2 text-slate-400" />
                                <div className="font-semibold">
                                    {activeFile.original_filename}
                                </div>
                                <div className="text-xs text-slate-500 mt-1">
                                    Preview unavailable for this file type.
                                </div>
                                <a
                                    href={fileUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="mt-3 inline-block text-sm font-semibold text-[#007AFF] hover:underline"
                                >
                                    Open in new tab
                                </a>
                            </div>
                        )}
                        {/* Vertical icon-only takeoff rail — pinned to the
                            right edge, vertically centred so it never
                            collides with the centred page-nav at the
                            bottom or the zoom/fit/edit bar at the top. */}
                        {/* iter-177 — right-side takeoff rail is now
                            gated to plain project PDFs; hidden on
                            schedules and as-built pages where drawing
                            doesn't apply. */}
                        {isPdf(activeFile) && !activeFile.is_asbuilt && !activeFile.schedule_no && (onUndoTakeoff || onDrawModeChange) && (
                            <div
                                className="absolute right-3 top-1/2 -translate-y-1/2 z-20 flex flex-col items-stretch rounded-full bg-[#0A1128]/92 backdrop-blur-xl border border-[#2A3655] shadow-lg overflow-hidden select-none"
                                data-testid="takeoff-toolbar"
                            >
                                {onDrawModeChange && (
                                    <>
                                        <DrawModeButton
                                            mode="area"
                                            Icon={Square}
                                            title="Draw area polygon — click vertices, Enter to finish"
                                            active={drawMode === "area"}
                                            onClick={() => onDrawModeChange(drawMode === "area" ? null : "area")}
                                        />
                                        <DrawModeButton
                                            mode="linear"
                                            Icon={Ruler}
                                            title="Draw linear run — click vertices, Enter to finish"
                                            active={drawMode === "linear"}
                                            onClick={() => onDrawModeChange(drawMode === "linear" ? null : "linear")}
                                        />
                                        <DrawModeButton
                                            mode="count"
                                            Icon={Dot}
                                            title="Drop count markers — click each instance, Enter to finish"
                                            active={drawMode === "count"}
                                            onClick={() => onDrawModeChange(drawMode === "count" ? null : "count")}
                                        />
                                        {onUndoTakeoff && (
                                            <span className="h-px w-6 bg-[#2A3655] self-center" />
                                        )}
                                    </>
                                )}
                                {onUndoTakeoff && (
                                    <button
                                        type="button"
                                        onClick={onUndoTakeoff}
                                        disabled={!canUndoTakeoff || undoingTakeoff}
                                        data-testid="takeoff-undo-btn"
                                        aria-label="Undo last takeoff edit"
                                        title={canUndoTakeoff ? "Undo last takeoff edit (⌘Z)" : "Nothing to undo"}
                                        className="w-11 h-11 inline-flex items-center justify-center text-white hover:bg-[#00A3FF]/15 hover:text-[#00A3FF] disabled:text-slate-600 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors active:scale-95"
                                    >
                                        {undoingTakeoff ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <Undo2 className="w-4 h-4" />
                                        )}
                                    </button>
                                )}
                            </div>
                        )}

                        {/* Live "Drawing …" status panel — anchored to the
                            bottom-right above the mobile bottom-toolbar so
                            it stays away from the centred page nav. */}
                        {isPdf(activeFile) && drawMode && (
                            <div
                                className="absolute right-3 bottom-20 md:bottom-4 z-20 select-none"
                                data-testid="takeoff-draw-status"
                            >
                                <DrawStatusPanel
                                    drawMode={drawMode}
                                    drawPoints={drawPoints}
                                    drawTrade={drawTrade}
                                    drawSaving={drawSaving}
                                    onTradeChange={onDrawTradeChange}
                                    onFinish={onDrawFinish}
                                    onCancel={onDrawCancel}
                                />
                            </div>
                        )}
                        {navPages.length > 1 && !activeFile?.is_asbuilt && !activeFile?.schedule_no && (
                            <div
                                className="absolute left-1/2 -translate-x-1/2 bottom-4 z-20 inline-flex items-center gap-0 rounded-full bg-[#0A1128]/92 backdrop-blur-xl border border-[#2A3655] shadow-lg overflow-hidden select-none"
                                data-testid="workspace-page-nav"
                            >
                                <button
                                    type="button"
                                    onClick={() => goToNav(-1)}
                                    disabled={navIndex <= 0}
                                    data-testid="workspace-prev-page"
                                    aria-label="Previous page"
                                    title="Previous page (← arrow key)"
                                    className="w-10 h-10 flex items-center justify-center text-white hover:bg-[#00A3FF]/15 hover:text-[#00A3FF] disabled:text-slate-600 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors active:scale-95"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                                <div
                                    className="px-3 text-[11px] text-slate-200 tabular-nums border-l border-r border-[#2A3655] h-10 flex items-center min-w-[96px] justify-center"
                                    data-testid="workspace-page-indicator"
                                >
                                    <span className="text-[#00A3FF] font-bold">
                                        {navIndex < 0 ? "—" : navIndex + 1}
                                    </span>
                                    <span className="text-slate-500 mx-1">/</span>
                                    <span className="text-slate-300 font-bold">
                                        {navPages.length}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => goToNav(1)}
                                    disabled={navIndex < 0 || navIndex >= navPages.length - 1}
                                    data-testid="workspace-next-page"
                                    aria-label="Next page"
                                    title="Next page (→ arrow key)"
                                    className="w-10 h-10 flex items-center justify-center text-white hover:bg-[#00A3FF]/15 hover:text-[#00A3FF] disabled:text-slate-600 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors active:scale-95"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </main>
    );
}


function DrawModeButton({ mode, Icon, title, active, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={`takeoff-draw-${mode}-btn`}
            aria-pressed={active}
            aria-label={title}
            title={title}
            className={`w-11 h-11 inline-flex items-center justify-center transition-colors active:scale-95 ${
                active
                    ? "bg-[#00A3FF] text-white"
                    : "text-white hover:bg-[#00A3FF]/15 hover:text-[#00A3FF]"
            }`}
        >
            <Icon className="w-4 h-4" />
        </button>
    );
}

/**
 * Floating panel above the toolbar while a draw is in progress. Shows the
 * vertex count + the trade selector + Finish/Cancel actions. Trade
 * selector tints the in-progress preview shape via the `drawTrade` state
 * upstream, so users can see colour feedback before committing.
 */
function DrawStatusPanel({ drawMode, drawPoints, drawTrade, drawSaving, onTradeChange, onFinish, onCancel }) {
    const min = { area: 3, linear: 2, count: 1 }[drawMode] || 1;
    const ready = drawPoints.length >= min;
    const tradeMeta = TRADES.find((t) => t.id === drawTrade) || TRADES[0];
    return (
        <div
            className="rounded-md bg-[#0A1128]/95 backdrop-blur-xl border border-[#2A3655] shadow-xl px-3 py-2 text-xs space-y-2 min-w-[220px]"
            data-testid="takeoff-draw-status"
        >
            <div className="flex items-center gap-2">
                <PenTool className="w-3.5 h-3.5 text-[#00A3FF]" />
                <span className="text-slate-300 font-bold uppercase tracking-wider text-[10px]">
                    Drawing {drawMode}
                </span>
                <span className="ml-auto text-slate-500 tabular-nums">
                    {drawPoints.length} pt{drawPoints.length === 1 ? "" : "s"}
                </span>
            </div>
            <div className="flex items-center gap-1.5">
                <span
                    className="inline-block w-3 h-3 rounded-full border border-white/20"
                    style={{ backgroundColor: tradeMeta.color }}
                />
                <select
                    value={drawTrade}
                    onChange={(e) => onTradeChange && onTradeChange(e.target.value)}
                    data-testid="takeoff-draw-trade-select"
                    className="flex-1 bg-[#131A2F] border border-[#2A3655] rounded-sm text-slate-100 text-[11px] px-2 py-1 focus:outline-none focus:border-[#00A3FF]"
                >
                    {TRADES.map((t) => (
                        <option key={t.id} value={t.id}>
                            {t.name}
                        </option>
                    ))}
                </select>
            </div>
            <div className="flex gap-1.5">
                <button
                    type="button"
                    onClick={onFinish}
                    disabled={!ready || drawSaving}
                    data-testid="takeoff-draw-finish-btn"
                    title="Finish drawing (Enter)"
                    className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-sm bg-emerald-500/90 hover:bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-wider disabled:opacity-40 disabled:cursor-not-allowed transition-colors active:scale-95"
                >
                    {drawSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    Finish
                </button>
                <button
                    type="button"
                    onClick={onCancel}
                    data-testid="takeoff-draw-cancel-btn"
                    title="Cancel (Esc)"
                    className="inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-sm bg-[#1C253C] hover:bg-[#223055] text-slate-200 hover:text-white text-[10px] font-bold uppercase tracking-wider transition-colors active:scale-95"
                >
                    <XIcon className="w-3 h-3" />
                    Cancel
                </button>
            </div>
            <div className="text-[10px] text-slate-500 leading-tight">
                {drawMode === "count"
                    ? "Click each instance to drop a marker, then Finish."
                    : `Click ${min}+ vertices on the drawing, then Finish.`}
            </div>
        </div>
    );
}
